import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import { createTicket } from "../Api/TicketApiOrg";
import { ticketDropdownApi } from "../Api/TicketApiOrg";

const TicketCreateModal = ({
  show,
  onClose,
  orgId,
  userDetails,
  refreshTickets,
}) => {
  const [loading, setLoading] = useState(false);

  // Dropdown options states
  const [typeOptions, setTypeOptions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  // Project and assignee states
  const [projectOptions, setProjectOptions] = useState([]);
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [submittedByOptions, setSubmittedByOptions] = useState([]);

  // Loading states
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [loadingSubmittedBy, setLoadingSubmittedBy] = useState(false);

  // Form state
  const [form, setForm] = useState({
    ticketName: "",
    description: "",
    priority: "Medium",
    ticketType: "",
    ticketReason: "",
    category: "",
    projectAssigned: "",
    assignedTo: [],
    submittedBy: "",
    attachments: [],
  });

  // Load ticket types on modal open
  useEffect(() => {
    if (show && orgId) {
      loadTicketTypes();
      loadSubmittedByOptions();
    }
  }, [show, orgId]);

  // Load projects when submittedBy changes
  useEffect(() => {
    if (form.submittedBy) {
      loadProjectsBySubmittedBy(form.submittedBy);
    } else {
      setProjectOptions([]);
      setForm((prev) => ({ ...prev, projectAssigned: "", assignedTo: [] }));
      setAssignedToOptions([]);
    }
  }, [form.submittedBy]);

  // Load assignees when projectAssigned changes
  useEffect(() => {
    if (form.projectAssigned) {
      loadAssigneesByProjects(form.projectAssigned);
    } else {
      setAssignedToOptions([]);
      setForm((prev) => ({ ...prev, assignedTo: [] }));
    }
  }, [form.projectAssigned]);

  // Load reasons when ticketType changes
  useEffect(() => {
    if (form.ticketType) {
      loadReasonsByType(form.ticketType);
    } else {
      setReasonOptions([]);
      setForm((prev) => ({ ...prev, ticketReason: "", category: "" }));
      setCategoryOptions([]);
    }
  }, [form.ticketType]);

  // Load categories when ticketReason changes
  useEffect(() => {
    if (form.ticketReason) {
      loadCategoriesByReason(form.ticketReason);
    } else {
      setCategoryOptions([]);
      setForm((prev) => ({ ...prev, category: "" }));
    }
  }, [form.ticketReason]);

  // Load ticket types from API
  const loadTicketTypes = async () => {
    setLoadingTypes(true);
    try {
      const types = await ticketDropdownApi.getTicketType(orgId);
      const formattedTypes = types.map((t) => ({
        value: t.type_GUID,
        label: t.ticketTypeName,
      }));
      // Sort alphabetically by label
      const sortedTypes = formattedTypes.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setTypeOptions(sortedTypes);
    } catch (error) {
      console.error("Failed to load ticket types:", error);
      toast.error("Failed to load ticket types");
    } finally {
      setLoadingTypes(false);
    }
  };

  // Load submitted by options
  const loadSubmittedByOptions = async () => {
    setLoadingSubmittedBy(true);
    try {
      const data = await ticketDropdownApi.getSubmittedBy(orgId);
      const uniqueMap = new Map();
      data.forEach((item) => {
        if (!uniqueMap.has(item.contactGuid)) {
          uniqueMap.set(item.contactGuid, {
            value: item.contactGuid,
            label: item.description,
          });
        }
      });
      const options = Array.from(uniqueMap.values());
      // Sort alphabetically by label
      const sortedOptions = options.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setSubmittedByOptions(sortedOptions);
    } catch (error) {
      console.error("Failed to load submitted by options:", error);
      toast.error("Failed to load submitter options");
    } finally {
      setLoadingSubmittedBy(false);
    }
  };

  // Load projects by submitted by
  const loadProjectsBySubmittedBy = async (submittedByValue) => {
    setLoadingProjects(true);
    try {
      const projects = await ticketDropdownApi.getProjectsBySubmittedBy(
        orgId,
        submittedByValue,
      );
      const formattedProjects = projects.map((project) => ({
        value: project.project_id,
        label: `${project.pr_Title} (${project.project_id})`,
      }));
      // Sort alphabetically by label
      const sortedProjects = formattedProjects.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setProjectOptions(sortedProjects);
    } catch (error) {
      console.error("Failed to load projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  // Load assignees by selected projects
  const loadAssigneesByProjects = async (projectIdsString) => {
    setLoadingAssignees(true);
    try {
      const projectIds = projectIdsString.split(",").map((id) => id.trim());
      const assigneeMap = new Map();

      for (const pid of projectIds) {
        const [team, customers, partners] = await Promise.all([
          ticketDropdownApi.getTeamAssigned(orgId, pid),
          ticketDropdownApi.getCustomer(orgId, pid),
          ticketDropdownApi.getPartner(orgId, pid),
        ]);

        // Team members
        team.forEach((item) => {
          if (!assigneeMap.has(item.contactGUID)) {
            assigneeMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Team)`,
            });
          }
        });

        // Customers
        customers.forEach((item) => {
          if (!assigneeMap.has(item.contactGUID)) {
            assigneeMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Customer)`,
            });
          }
        });

        // Partners
        partners.forEach((item) => {
          if (!assigneeMap.has(item.contactGUID)) {
            assigneeMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Partner)`,
            });
          }
        });
      }

      const options = Array.from(assigneeMap.values());
      // Sort alphabetically by label
      const sortedOptions = options.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setAssignedToOptions(sortedOptions);
    } catch (error) {
      console.error("Failed to load assignees:", error);
      toast.error("Failed to load assignee options");
    } finally {
      setLoadingAssignees(false);
    }
  };

  // Load reasons by ticket type
  const loadReasonsByType = async (typeGuid) => {
    setLoadingReasons(true);
    try {
      const reasons = await ticketDropdownApi.getTicketLoggedType(
        orgId,
        typeGuid,
      );
      const formattedReasons = reasons.map((r) => ({
        value: r.ticket_log_guid,
        label: r.ticketdescription,
      }));
      // Sort alphabetically by label
      const sortedReasons = formattedReasons.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setReasonOptions(sortedReasons);
    } catch (error) {
      console.error("Failed to load reasons:", error);
      toast.error("Failed to load ticket reasons");
    } finally {
      setLoadingReasons(false);
    }
  };

  // Load categories by ticket reason
  const loadCategoriesByReason = async (reasonGuid) => {
    setLoadingCategories(true);
    try {
      const categories = await ticketDropdownApi.getTicketCategory(
        orgId,
        reasonGuid,
      );
      const formattedCategories = categories.map((c) => ({
        value: c.category_guid,
        label: c.ticketcategoryname,
      }));
      // Sort alphabetically by label
      const sortedCategories = formattedCategories.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setCategoryOptions(sortedCategories);
    } catch (error) {
      console.error("Failed to load categories:", error);
      toast.error("Failed to load categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Handle ticket type change
  const handleTypeChange = (selected) => {
    const typeGuid = selected?.value || "";
    setForm({
      ...form,
      ticketType: typeGuid,
      ticketReason: "",
      category: "",
    });
    setReasonOptions([]);
    setCategoryOptions([]);
  };

  // Handle reason change
  const handleReasonChange = (selected) => {
    const reasonGuid = selected?.value || "";
    setForm({
      ...form,
      ticketReason: reasonGuid,
      category: "",
    });
    setCategoryOptions([]);
  };

  // Handle category change
  const handleCategoryChange = (selected) => {
    setForm({
      ...form,
      category: selected?.value || "",
    });
  };

  // Handle submitted by change
  const handleSubmittedByChange = (selected) => {
    const submittedByValue = selected?.value || "";
    setForm({
      ...form,
      submittedBy: submittedByValue,
      projectAssigned: "",
      assignedTo: [],
    });
    setProjectOptions([]);
    setAssignedToOptions([]);
  };

  // Handle project change
  const handleProjectChange = (selected) => {
    const ids = selected ? selected.map((o) => o.value) : [];
    setForm({
      ...form,
      projectAssigned: ids.join(","),
      assignedTo: [],
    });
    setAssignedToOptions([]);
  };

  // Handle assigned to change
  const handleAssignedToChange = (selected) => {
    setForm({
      ...form,
      assignedTo: selected ? selected.map((o) => o.value) : [],
    });
  };

  // Create ticket
  const handleCreateTicket = async () => {
    // Validation
    if (!form.ticketName.trim()) {
      toast.error("Ticket Name is required");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }
    if (!form.ticketType) {
      toast.error("Please select Ticket Type");
      return;
    }
    if (!form.ticketReason) {
      toast.error("Please select Ticket Reason");
      return;
    }
    if (!form.category) {
      toast.error("Please select Category");
      return;
    }
    if (!form.submittedBy) {
      toast.error("Please select Submitted By");
      return;
    }
    if (!form.projectAssigned) {
      toast.error("Please select at least one Project");
      return;
    }
    if (form.assignedTo.length === 0) {
      toast.error("Please select at least one Assignee");
      return;
    }

    

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("OrgId", orgId);
      formData.append("TicketName", form.ticketName);
      formData.append("Description", form.description);
      formData.append("Priority", form.priority);
      formData.append("TicketType", form.ticketType);
      formData.append("TicketReason", form.ticketReason);
      formData.append("Category", form.category);
      formData.append("SubmittedBy", form.submittedBy);
      formData.append("AssignedTo", form.assignedTo.join(","));
      formData.append("ProjectAssigned", form.projectAssigned);
      formData.append("CreatedBy", userDetails.userId);
      formData.append("FromMail", userDetails.email);
      // Default status for new tickets - "Open" status GUID
      formData.append("Status", "53e33758-4502-4054-84c9-26abc56551a6");
      formData.append("SubStatus", "d11eaad1-c009-47f6-ac7b-30d3c1b5a54a");
      if (form.attachments && form.attachments.length > 0) {
        for (let i = 0; i < form.attachments.length; i++) {
          formData.append("Attachments", form.attachments[i]);
        }
      }

      await createTicket(formData);
      toast.success("Ticket created successfully");
      refreshTickets();
      onClose();

      // Reset form
      setForm({
        ticketName: "",
        description: "",
        priority: "Medium",
        ticketType: "",
        ticketReason: "",
        category: "",
        projectAssigned: "",
        assignedTo: [],
        submittedBy: "",
      });
      setReasonOptions([]);
      setCategoryOptions([]);
      setProjectOptions([]);
      setAssignedToOptions([]);
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast.error(error.response?.data?.Message || "Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  // Get selected project options for display
  const getSelectedProjectOptions = () => {
    if (!form.projectAssigned) return [];
    const selectedIds = form.projectAssigned.split(",");
    return projectOptions.filter((opt) => selectedIds.includes(opt.value));
  };

  // Get selected assigned to options for display
  const getSelectedAssignedToOptions = () => {
    return assignedToOptions.filter((opt) =>
      form.assignedTo.includes(opt.value),
    );
  };

  // Get project count text
  const getProjectCountText = () => {
    const selectedProjects =
      form.projectAssigned?.split(",").filter((p) => p) || [];
    const totalProjects = projectOptions.length;
    if (selectedProjects.length === 0) {
      return `0/${totalProjects} projects selected`;
    }
    return `${selectedProjects.length}/${totalProjects} projects selected`;
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      dialogClassName="custom-modal-dialog-TEM"
    >
      <Modal.Header closeButton>
        <Modal.Title>Create New Ticket</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Ticket Name */}
          <Form.Group className="mb-3">
            <Form.Label>
              Ticket Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.ticketName}
              onChange={(e) => handleFieldChange("ticketName", e.target.value)}
              placeholder="Enter ticket name"
            />
          </Form.Group>
          {/* Description */}
          <Form.Group className="mb-3">
            <Form.Label>
              Description <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => handleFieldChange("description", e.target.value)}
              placeholder="Enter ticket description"
            />
          </Form.Group>
          {/* Priority and Submitted By */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={form.priority}
                  onChange={(e) =>
                    handleFieldChange("priority", e.target.value)
                  }
                >
                  <option value="Low">Low</option>
                  <option value="Normal">Normal</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Submitted By <span className="text-danger">*</span>
                </Form.Label>
                {loadingSubmittedBy ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  <Select
                    options={submittedByOptions}
                    value={
                      submittedByOptions.find(
                        (opt) => opt.value === form.submittedBy,
                      ) || null
                    }
                    onChange={handleSubmittedByChange}
                    placeholder="Select submitter..."
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                )}
              </Form.Group>
            </Col>
          </Row>
          {/* Ticket Type, Reason, Category */}
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Ticket Type <span className="text-danger">*</span>
                </Form.Label>
                {loadingTypes ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  <Select
                    options={typeOptions}
                    value={
                      typeOptions.find(
                        (opt) => opt.value === form.ticketType,
                      ) || null
                    }
                    onChange={handleTypeChange}
                    placeholder="Select Type"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Ticket Reason <span className="text-danger">*</span>
                </Form.Label>
                {loadingReasons ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  <Select
                    options={reasonOptions}
                    value={
                      reasonOptions.find(
                        (opt) => opt.value === form.ticketReason,
                      ) || null
                    }
                    onChange={handleReasonChange}
                    placeholder="Select Reason"
                    isDisabled={!form.ticketType}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                {loadingCategories ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  <Select
                    options={categoryOptions}
                    value={
                      categoryOptions.find(
                        (opt) => opt.value === form.category,
                      ) || null
                    }
                    onChange={handleCategoryChange}
                    placeholder="Select Category"
                    isDisabled={!form.ticketReason}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                )}
              </Form.Group>
            </Col>
          </Row>
          {/* Project Assigned */}
          <Form.Group className="mb-3">
            <Form.Label>
              Project Assigned <span className="text-danger">*</span>
            </Form.Label>
            {loadingProjects ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading projects...</span>
              </div>
            ) : (
              <>
                <Select
                  isMulti
                  options={projectOptions}
                  value={getSelectedProjectOptions()}
                  onChange={handleProjectChange}
                  placeholder={
                    form.submittedBy
                      ? "Select projects..."
                      : "Please select Submitted By first"
                  }
                  isDisabled={!form.submittedBy || loadingProjects}
                  noOptionsMessage={() =>
                    !form.submittedBy
                      ? "Please select Submitted By first"
                      : "No projects available for this submitter"
                  }
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                {form.submittedBy && projectOptions.length > 0 && (
                  <Form.Text className="text-muted">
                    <small className="text-primary">
                      {getProjectCountText()}
                    </small>
                  </Form.Text>
                )}
              </>
            )}
          </Form.Group>
          {/* Assigned To */}
          <Form.Group className="mb-3">
            <Form.Label>
              Assigned To <span className="text-danger">*</span>
            </Form.Label>
            {loadingAssignees ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading assignees...</span>
              </div>
            ) : (
              <>
                <Select
                  isMulti
                  options={assignedToOptions}
                  value={getSelectedAssignedToOptions()}
                  onChange={handleAssignedToChange}
                  placeholder={
                    form.projectAssigned
                      ? "Select assignees..."
                      : "Please select a project first"
                  }
                  isDisabled={!form.projectAssigned || loadingAssignees}
                  noOptionsMessage={() =>
                    !form.projectAssigned
                      ? "Please select a project first"
                      : "No assignees available for selected project(s)"
                  }
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                {form.projectAssigned && assignedToOptions.length > 0 && (
                  <Form.Text className="text-muted">
                    <small>
                      {assignedToOptions.length} assignee(s) available
                    </small>
                  </Form.Text>
                )}
              </>
            )}
          </Form.Group>
         
          <Form.Group className="mb-3">
            <Form.Label>Attachments</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                setForm((prev) => ({ ...prev, attachments: files }));
              }}
            />
            <Form.Text className="text-muted">
              You can select multiple files. Max size per file: 10MB
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateTicket}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            "Create Ticket"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketCreateModal;

// import { useState, useEffect } from "react";
// import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
// import { toast } from "react-toastify";
// import Select from "react-select";
// import { createTicket } from "../Api/TicketApiOrg";
// import { ticketDropdownApi } from "../Api/TicketApiOrg";

// const TicketCreateModal = ({
//   show,
//   onClose,
//   orgId,
//   userDetails,
//   refreshTickets,
// }) => {
//   const [loading, setLoading] = useState(false);

//   // Dropdown options states
//   const [typeOptions, setTypeOptions] = useState([]);
//   const [reasonOptions, setReasonOptions] = useState([]);
//   const [categoryOptions, setCategoryOptions] = useState([]);

//   // Project and assignee states
//   const [projectOptions, setProjectOptions] = useState([]);
//   const [assignedToOptions, setAssignedToOptions] = useState([]);
//   const [submittedByOptions, setSubmittedByOptions] = useState([]);

//   // Loading states
//   const [loadingTypes, setLoadingTypes] = useState(false);
//   const [loadingReasons, setLoadingReasons] = useState(false);
//   const [loadingCategories, setLoadingCategories] = useState(false);
//   const [loadingProjects, setLoadingProjects] = useState(false);
//   const [loadingAssignees, setLoadingAssignees] = useState(false);
//   const [loadingSubmittedBy, setLoadingSubmittedBy] = useState(false);

//   // Form state
//   const [form, setForm] = useState({
//     ticketName: "",
//     description: "",
//     priority: "Medium",
//     ticketType: "",
//     ticketReason: "",
//     category: "",
//     projectAssigned: "",
//     assignedTo: [],
//     submittedBy: "",
//   });

//   // Load ticket types on modal open
//   useEffect(() => {
//     if (show && orgId) {
//       loadTicketTypes();
//       loadSubmittedByOptions();
//     }
//   }, [show, orgId]);

//   // Load projects when submittedBy changes
//   useEffect(() => {
//     if (form.submittedBy) {
//       loadProjectsBySubmittedBy(form.submittedBy);
//     } else {
//       setProjectOptions([]);
//       setForm((prev) => ({ ...prev, projectAssigned: "", assignedTo: [] }));
//       setAssignedToOptions([]);
//     }
//   }, [form.submittedBy]);

//   // Load assignees when projectAssigned changes
//   useEffect(() => {
//     if (form.projectAssigned) {
//       loadAssigneesByProjects(form.projectAssigned);
//     } else {
//       setAssignedToOptions([]);
//       setForm((prev) => ({ ...prev, assignedTo: [] }));
//     }
//   }, [form.projectAssigned]);

//   // Load reasons when ticketType changes
//   useEffect(() => {
//     if (form.ticketType) {
//       loadReasonsByType(form.ticketType);
//     } else {
//       setReasonOptions([]);
//       setForm((prev) => ({ ...prev, ticketReason: "", category: "" }));
//       setCategoryOptions([]);
//     }
//   }, [form.ticketType]);

//   // Load categories when ticketReason changes
//   useEffect(() => {
//     if (form.ticketReason) {
//       loadCategoriesByReason(form.ticketReason);
//     } else {
//       setCategoryOptions([]);
//       setForm((prev) => ({ ...prev, category: "" }));
//     }
//   }, [form.ticketReason]);

//   // Load ticket types from API
//   const loadTicketTypes = async () => {
//     setLoadingTypes(true);
//     try {
//       const types = await ticketDropdownApi.getTicketType(orgId);
//       const formattedTypes = types.map((t) => ({
//         value: t.type_GUID,
//         label: t.ticketTypeName,
//       }));
//       setTypeOptions(formattedTypes);
//     } catch (error) {
//       console.error("Failed to load ticket types:", error);
//       toast.error("Failed to load ticket types");
//     } finally {
//       setLoadingTypes(false);
//     }
//   };

//   // Load submitted by options
//   const loadSubmittedByOptions = async () => {
//     setLoadingSubmittedBy(true);
//     try {
//       const data = await ticketDropdownApi.getSubmittedBy(orgId);
//       const uniqueMap = new Map();
//       data.forEach((item) => {
//         if (!uniqueMap.has(item.contactGuid)) {
//           uniqueMap.set(item.contactGuid, {
//             value: item.contactGuid,
//             label: item.description,
//           });
//         }
//       });
//       const options = Array.from(uniqueMap.values()).sort((a, b) =>
//         a.label.localeCompare(b.label),
//       );
//       setSubmittedByOptions(options);
//     } catch (error) {
//       console.error("Failed to load submitted by options:", error);
//       toast.error("Failed to load submitter options");
//     } finally {
//       setLoadingSubmittedBy(false);
//     }
//   };

//   // Load projects by submitted by
//   const loadProjectsBySubmittedBy = async (submittedByValue) => {
//     setLoadingProjects(true);
//     try {
//       const projects = await ticketDropdownApi.getProjectsBySubmittedBy(
//         orgId,
//         submittedByValue,
//       );
//       const formattedProjects = projects.map((project) => ({
//         value: project.project_id,
//         label: `${project.pr_Title} (${project.project_id})`,
//       }));
//       setProjectOptions(formattedProjects);
//     } catch (error) {
//       console.error("Failed to load projects:", error);
//       toast.error("Failed to load projects");
//     } finally {
//       setLoadingProjects(false);
//     }
//   };

//   // Load assignees by selected projects
//   const loadAssigneesByProjects = async (projectIdsString) => {
//     setLoadingAssignees(true);
//     try {
//       const projectIds = projectIdsString.split(",").map((id) => id.trim());
//       const assigneeMap = new Map();

//       for (const pid of projectIds) {
//         const [team, customers, partners] = await Promise.all([
//           ticketDropdownApi.getTeamAssigned(orgId, pid),
//           ticketDropdownApi.getCustomer(orgId, pid),
//           ticketDropdownApi.getPartner(orgId, pid),
//         ]);

//         // Team members
//         team.forEach((item) => {
//           if (!assigneeMap.has(item.contactGUID)) {
//             assigneeMap.set(item.contactGUID, {
//               value: item.contactGUID,
//               label: `${item.contactName} (Team)`,
//             });
//           }
//         });

//         // Customers
//         customers.forEach((item) => {
//           if (!assigneeMap.has(item.contactGUID)) {
//             assigneeMap.set(item.contactGUID, {
//               value: item.contactGUID,
//               label: `${item.contactName} (Customer)`,
//             });
//           }
//         });

//         // Partners
//         partners.forEach((item) => {
//           if (!assigneeMap.has(item.contactGUID)) {
//             assigneeMap.set(item.contactGUID, {
//               value: item.contactGUID,
//               label: `${item.contactName} (Partner)`,
//             });
//           }
//         });
//       }

//       const options = Array.from(assigneeMap.values()).sort((a, b) =>
//         a.label.localeCompare(b.label),
//       );
//       setAssignedToOptions(options);
//     } catch (error) {
//       console.error("Failed to load assignees:", error);
//       toast.error("Failed to load assignee options");
//     } finally {
//       setLoadingAssignees(false);
//     }
//   };

//   // Load reasons by ticket type
//   const loadReasonsByType = async (typeGuid) => {
//     setLoadingReasons(true);
//     try {
//       const reasons = await ticketDropdownApi.getTicketLoggedType(
//         orgId,
//         typeGuid,
//       );
//       const formattedReasons = reasons.map((r) => ({
//         value: r.ticket_log_guid,
//         label: r.ticketdescription,
//       }));
//       setReasonOptions(formattedReasons);
//     } catch (error) {
//       console.error("Failed to load reasons:", error);
//       toast.error("Failed to load ticket reasons");
//     } finally {
//       setLoadingReasons(false);
//     }
//   };

//   // Load categories by ticket reason
//   const loadCategoriesByReason = async (reasonGuid) => {
//     setLoadingCategories(true);
//     try {
//       const categories = await ticketDropdownApi.getTicketCategory(
//         orgId,
//         reasonGuid,
//       );
//       const formattedCategories = categories.map((c) => ({
//         value: c.category_guid,
//         label: c.ticketcategoryname,
//       }));
//       setCategoryOptions(formattedCategories);
//     } catch (error) {
//       console.error("Failed to load categories:", error);
//       toast.error("Failed to load categories");
//     } finally {
//       setLoadingCategories(false);
//     }
//   };

//   // Handle form field changes
//   const handleFieldChange = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   // Handle ticket type change
//   const handleTypeChange = (selected) => {
//     const typeGuid = selected?.value || "";
//     setForm({
//       ...form,
//       ticketType: typeGuid,
//       ticketReason: "",
//       category: "",
//     });
//     setReasonOptions([]);
//     setCategoryOptions([]);
//   };

//   // Handle reason change
//   const handleReasonChange = (selected) => {
//     const reasonGuid = selected?.value || "";
//     setForm({
//       ...form,
//       ticketReason: reasonGuid,
//       category: "",
//     });
//     setCategoryOptions([]);
//   };

//   // Handle category change
//   const handleCategoryChange = (selected) => {
//     setForm({
//       ...form,
//       category: selected?.value || "",
//     });
//   };

//   // Handle submitted by change
//   const handleSubmittedByChange = (selected) => {
//     const submittedByValue = selected?.value || "";
//     setForm({
//       ...form,
//       submittedBy: submittedByValue,
//       projectAssigned: "",
//       assignedTo: [],
//     });
//     setProjectOptions([]);
//     setAssignedToOptions([]);
//   };

//   // Handle project change
//   const handleProjectChange = (selected) => {
//     const ids = selected ? selected.map((o) => o.value) : [];
//     setForm({
//       ...form,
//       projectAssigned: ids.join(","),
//       assignedTo: [],
//     });
//     setAssignedToOptions([]);
//   };

//   // Handle assigned to change
//   const handleAssignedToChange = (selected) => {
//     setForm({
//       ...form,
//       assignedTo: selected ? selected.map((o) => o.value) : [],
//     });
//   };

//   // Create ticket
//   const handleCreateTicket = async () => {
//     // Validation
//     if (!form.ticketName.trim()) {
//       toast.error("Ticket Name is required");
//       return;
//     }
//     if (!form.description.trim()) {
//       toast.error("Description is required");
//       return;
//     }
//     if (!form.ticketType) {
//       toast.error("Please select Ticket Type");
//       return;
//     }
//     if (!form.ticketReason) {
//       toast.error("Please select Ticket Reason");
//       return;
//     }
//     if (!form.category) {
//       toast.error("Please select Category");
//       return;
//     }
//     if (!form.submittedBy) {
//       toast.error("Please select Submitted By");
//       return;
//     }
//     if (!form.projectAssigned) {
//       toast.error("Please select at least one Project");
//       return;
//     }
//     if (form.assignedTo.length === 0) {
//       toast.error("Please select at least one Assignee");
//       return;
//     }

//     setLoading(true);

//     try {
//       const formData = new FormData();
//       formData.append("OrgId", orgId);
//       formData.append("TicketName", form.ticketName);
//       formData.append("Description", form.description);
//       formData.append("Priority", form.priority);
//       formData.append("TicketType", form.ticketType);
//       formData.append("TicketReason", form.ticketReason);
//       formData.append("Category", form.category);
//       formData.append("SubmittedBy", form.submittedBy);
//       formData.append("AssignedTo", form.assignedTo.join(","));
//       formData.append("ProjectAssigned", form.projectAssigned);
//       formData.append("CreatedBy", userDetails.userId);
//       formData.append("FromMail", userDetails.email);
//       // Default status for new tickets - "Open" status GUID
//       formData.append("Status", "53e33758-4502-4054-84c9-26abc56551a6");
//       formData.append("SubStatus", "d11eaad1-c009-47f6-ac7b-30d3c1b5a54a");

//       await createTicket(formData);
//       toast.success("Ticket created successfully");
//       refreshTickets();
//       onClose();

//       // Reset form
//       setForm({
//         ticketName: "",
//         description: "",
//         priority: "Medium",
//         ticketType: "",
//         ticketReason: "",
//         category: "",
//         projectAssigned: "",
//         assignedTo: [],
//         submittedBy: "",
//       });
//       setReasonOptions([]);
//       setCategoryOptions([]);
//       setProjectOptions([]);
//       setAssignedToOptions([]);
//     } catch (error) {
//       console.error("Error creating ticket:", error);
//       toast.error(error.response?.data?.Message || "Failed to create ticket");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Get selected project options for display
//   const getSelectedProjectOptions = () => {
//     if (!form.projectAssigned) return [];
//     const selectedIds = form.projectAssigned.split(",");
//     return projectOptions.filter((opt) => selectedIds.includes(opt.value));
//   };

//   // Get selected assigned to options for display
//   const getSelectedAssignedToOptions = () => {
//     return assignedToOptions.filter((opt) =>
//       form.assignedTo.includes(opt.value),
//     );
//   };

//   // Get project count text
//   const getProjectCountText = () => {
//     const selectedProjects =
//       form.projectAssigned?.split(",").filter((p) => p) || [];
//     const totalProjects = projectOptions.length;
//     if (selectedProjects.length === 0) {
//       return `0/${totalProjects} projects selected`;
//     }
//     return `${selectedProjects.length}/${totalProjects} projects selected`;
//   };

//   return (
//     <Modal
//       show={show}
//       onHide={onClose}
//       size="lg"
//       dialogClassName="custom-modal-dialog-TEM"
//     >
//       <Modal.Header closeButton>
//         <Modal.Title>Create New Ticket</Modal.Title>
//       </Modal.Header>

//       <Modal.Body>
//         <Form>
//           {/* Ticket Name */}
//           <Form.Group className="mb-3">
//             <Form.Label>
//               Ticket Name <span className="text-danger">*</span>
//             </Form.Label>
//             <Form.Control
//               type="text"
//               value={form.ticketName}
//               onChange={(e) => handleFieldChange("ticketName", e.target.value)}
//               placeholder="Enter ticket name"
//             />
//           </Form.Group>

//           {/* Description */}
//           <Form.Group className="mb-3">
//             <Form.Label>
//               Description <span className="text-danger">*</span>
//             </Form.Label>
//             <Form.Control
//               as="textarea"
//               rows={3}
//               value={form.description}
//               onChange={(e) => handleFieldChange("description", e.target.value)}
//               placeholder="Enter ticket description"
//             />
//           </Form.Group>

//           {/* Priority and Submitted By */}
//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Priority</Form.Label>
//                 <Form.Select
//                   value={form.priority}
//                   onChange={(e) =>
//                     handleFieldChange("priority", e.target.value)
//                   }
//                 >
//                   <option value="Low">Low</option>
//                   <option value="Normal">Normal</option>
//                   <option value="Medium">Medium</option>
//                   <option value="High">High</option>
//                   <option value="Critical">Critical</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Submitted By <span className="text-danger">*</span>
//                 </Form.Label>
//                 {loadingSubmittedBy ? (
//                   <div className="text-center py-2">
//                     <Spinner animation="border" size="sm" />
//                   </div>
//                 ) : (
//                   <Select
//                     options={submittedByOptions}
//                     value={
//                       submittedByOptions.find(
//                         (opt) => opt.value === form.submittedBy,
//                       ) || null
//                     }
//                     onChange={handleSubmittedByChange}
//                     placeholder="Select submitter..."
//                     isClearable
//                     menuPortalTarget={document.body}
//                     styles={{
//                       menuPortal: (base) => ({ ...base, zIndex: 9999 }),
//                     }}
//                   />
//                 )}
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Ticket Type, Reason, Category */}
//           <Row>
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Ticket Type <span className="text-danger">*</span>
//                 </Form.Label>
//                 {loadingTypes ? (
//                   <div className="text-center py-2">
//                     <Spinner animation="border" size="sm" />
//                   </div>
//                 ) : (
//                   <Select
//                     options={typeOptions}
//                     value={
//                       typeOptions.find(
//                         (opt) => opt.value === form.ticketType,
//                       ) || null
//                     }
//                     onChange={handleTypeChange}
//                     placeholder="Select Type"
//                     menuPortalTarget={document.body}
//                     styles={{
//                       menuPortal: (base) => ({ ...base, zIndex: 9999 }),
//                     }}
//                   />
//                 )}
//               </Form.Group>
//             </Col>
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Ticket Reason <span className="text-danger">*</span>
//                 </Form.Label>
//                 {loadingReasons ? (
//                   <div className="text-center py-2">
//                     <Spinner animation="border" size="sm" />
//                   </div>
//                 ) : (
//                   <Select
//                     options={reasonOptions}
//                     value={
//                       reasonOptions.find(
//                         (opt) => opt.value === form.ticketReason,
//                       ) || null
//                     }
//                     onChange={handleReasonChange}
//                     placeholder="Select Reason"
//                     isDisabled={!form.ticketType}
//                     menuPortalTarget={document.body}
//                     styles={{
//                       menuPortal: (base) => ({ ...base, zIndex: 9999 }),
//                     }}
//                   />
//                 )}
//               </Form.Group>
//             </Col>
//             <Col md={4}>
//               <Form.Group className="mb-3">
//                 <Form.Label>
//                   Category <span className="text-danger">*</span>
//                 </Form.Label>
//                 {loadingCategories ? (
//                   <div className="text-center py-2">
//                     <Spinner animation="border" size="sm" />
//                   </div>
//                 ) : (
//                   <Select
//                     options={categoryOptions}
//                     value={
//                       categoryOptions.find(
//                         (opt) => opt.value === form.category,
//                       ) || null
//                     }
//                     onChange={handleCategoryChange}
//                     placeholder="Select Category"
//                     isDisabled={!form.ticketReason}
//                     menuPortalTarget={document.body}
//                     styles={{
//                       menuPortal: (base) => ({ ...base, zIndex: 9999 }),
//                     }}
//                   />
//                 )}
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Project Assigned */}
//           <Form.Group className="mb-3">
//             <Form.Label>
//               Project Assigned <span className="text-danger">*</span>
//             </Form.Label>
//             {loadingProjects ? (
//               <div className="text-center py-2">
//                 <Spinner animation="border" size="sm" />
//                 <span className="ms-2">Loading projects...</span>
//               </div>
//             ) : (
//               <>
//                 <Select
//                   isMulti
//                   options={projectOptions}
//                   value={getSelectedProjectOptions()}
//                   onChange={handleProjectChange}
//                   placeholder={
//                     form.submittedBy
//                       ? "Select projects..."
//                       : "Please select Submitted By first"
//                   }
//                   isDisabled={!form.submittedBy || loadingProjects}
//                   noOptionsMessage={() =>
//                     !form.submittedBy
//                       ? "Please select Submitted By first"
//                       : "No projects available for this submitter"
//                   }
//                   menuPortalTarget={document.body}
//                   styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
//                 />
//                 {form.submittedBy && projectOptions.length > 0 && (
//                   <Form.Text className="text-muted">
//                     <small className="text-primary">
//                       {getProjectCountText()}
//                     </small>
//                   </Form.Text>
//                 )}
//               </>
//             )}
//           </Form.Group>

//           {/* Assigned To */}
//           <Form.Group className="mb-3">
//             <Form.Label>
//               Assigned To <span className="text-danger">*</span>
//             </Form.Label>
//             {loadingAssignees ? (
//               <div className="text-center py-2">
//                 <Spinner animation="border" size="sm" />
//                 <span className="ms-2">Loading assignees...</span>
//               </div>
//             ) : (
//               <>
//                 <Select
//                   isMulti
//                   options={assignedToOptions}
//                   value={getSelectedAssignedToOptions()}
//                   onChange={handleAssignedToChange}
//                   placeholder={
//                     form.projectAssigned
//                       ? "Select assignees..."
//                       : "Please select a project first"
//                   }
//                   isDisabled={!form.projectAssigned || loadingAssignees}
//                   noOptionsMessage={() =>
//                     !form.projectAssigned
//                       ? "Please select a project first"
//                       : "No assignees available for selected project(s)"
//                   }
//                   menuPortalTarget={document.body}
//                   styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
//                 />
//                 {form.projectAssigned && assignedToOptions.length > 0 && (
//                   <Form.Text className="text-muted">
//                     <small>
//                       {assignedToOptions.length} assignee(s) available
//                     </small>
//                   </Form.Text>
//                 )}
//               </>
//             )}
//           </Form.Group>
//         </Form>
//       </Modal.Body>

//       <Modal.Footer>
//         <Button variant="secondary" onClick={onClose} disabled={loading}>
//           Cancel
//         </Button>
//         <Button
//           variant="primary"
//           onClick={handleCreateTicket}
//           disabled={loading}
//         >
//           {loading ? (
//             <>
//               <Spinner animation="border" size="sm" className="me-2" />
//               Creating...
//             </>
//           ) : (
//             "Create Ticket"
//           )}
//         </Button>
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default TicketCreateModal;
